// ============================================================================
// kozlemenyView.js — a KÖZLEMÉNY felugró ablaka (2026-10-25, Tamás kérésére): az admin üzenete, amit a tag TUDOMÁSUL VESZ.
//
// Önálló réteg a <body>-n (mint a cetliView.js): a chat újrarajzolásai nem zavarják. A szöveg MINDIG textContent-tel kerül a
// felületre (soha nem innerHTML). A "Később" bezárja (a közlemény megmarad, a következő belépéskor újra felugrik, a fejlécben a 📌
// számláló jelzi); csak a "Tudomásul vettem" gomb veszi tudomásul (a szerveren rögzül, az admin látja).
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { feladoNev } from "./chatView.js";
import { log } from "./log.js";

function datum(ms, uiLang) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(localeFor(uiLang), { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return "";
  }
}

function elem(tag, osztaly, szoveg) {
  const n = document.createElement(tag);
  if (osztaly) n.className = osztaly;
  if (szoveg !== undefined) n.textContent = szoveg;
  return n;
}

let olvaso = null;

export function kozlemenyOlvasoNyitva() {
  return olvaso !== null;
}

export function zarjBeKozlemenyOlvasot() {
  if (olvaso) olvaso.bezar();
}

/**
 * Megnyitja a közlemény-olvasót, vagy - ha nyitva van - frissíti (új érkezett / egyet tudomásul vettek).
 * @param {object} p { uiLang, tartalomNyelv, kozlemenyek (legrégebbi elöl), onTudomas(annId): Promise, kijelolt? }
 */
export function nyissKozlemenyOlvasot(p) {
  if (olvaso) {
    olvaso.frissit(p);
    return olvaso;
  }
  const gyoker = elem("div", "cetli-hatter kozlemeny-hatter");
  gyoker.setAttribute("role", "dialog");
  const kartya = elem("div", "cetli-kartya kozlemeny-kartya");
  gyoker.appendChild(kartya);

  let allapot = { ...p, index: 0, eredetiMutat: false, foglalt: false, hiba: null };

  function rajzol() {
    const { uiLang, tartalomNyelv, kozlemenyek } = allapot;
    if (kozlemenyek.length === 0) { bezar(); return; }
    allapot.index = Math.max(0, Math.min(allapot.index, kozlemenyek.length - 1));
    const k = kozlemenyek[allapot.index];
    kartya.textContent = "";

    const fej = elem("div", "cetli-fej");
    fej.appendChild(elem("div", "cetli-cim", "📌 " + t(uiLang, "ann.title")));
    if (kozlemenyek.length > 1) {
      const lapozo = elem("div", "cetli-lapozo");
      const gomb = (jel, irany, tiltva) => {
        const b = elem("button", "cetli-lapozo-gomb", jel);
        b.type = "button";
        b.setAttribute("aria-label", jel);
        b.disabled = tiltva;
        b.addEventListener("click", () => { allapot.index += irany; allapot.eredetiMutat = false; rajzol(); });
        return b;
      };
      lapozo.appendChild(gomb("‹", -1, allapot.index === 0));
      lapozo.appendChild(elem("span", "cetli-szamlalo", `${allapot.index + 1}/${kozlemenyek.length}`));
      lapozo.appendChild(gomb("›", 1, allapot.index === kozlemenyek.length - 1));
      fej.appendChild(lapozo);
    }
    kartya.appendChild(fej);

    const kuldo = feladoNev(k, tartalomNyelv, uiLang);
    kartya.appendChild(elem("div", "cetli-meta", [t(uiLang, "ann.from", { name: kuldo }), datum(k.createdAt, uiLang)].filter(Boolean).join(" · ")));

    const fordithato = Boolean(k.translatedText);
    const eredetiLatszik = fordithato && allapot.eredetiMutat;
    const szoveg = elem("div", "cetli-szoveg", fordithato && !eredetiLatszik ? k.translatedText : k.text);
    const nyelv = fordithato && !eredetiLatszik ? k.translatedLang : k.sourceLang;
    if (nyelv) szoveg.setAttribute("lang", nyelv);
    kartya.appendChild(szoveg);

    if (fordithato) {
      const valto = elem("button", "cetli-valto", t(uiLang, eredetiLatszik ? "message.showTranslation" : "message.showOriginal"));
      valto.type = "button";
      valto.setAttribute("aria-pressed", eredetiLatszik ? "true" : "false");
      valto.addEventListener("click", () => { allapot.eredetiMutat = !allapot.eredetiMutat; rajzol(); });
      kartya.appendChild(valto);
    }
    if (allapot.hiba) kartya.appendChild(elem("div", "cetli-hiba", allapot.hiba));

    const gombok = elem("div", "cetli-gombok");
    const kesobb = elem("button", "cetli-megtart", t(uiLang, "ann.later"));
    kesobb.type = "button";
    kesobb.addEventListener("click", bezar);
    const tudomas = elem("button", "cetli-eldob kozlemeny-tudomas", t(uiLang, "ann.ack"));
    tudomas.type = "button";
    tudomas.disabled = allapot.foglalt;
    tudomas.addEventListener("click", async () => {
      if (allapot.foglalt) return;
      allapot.foglalt = true;
      allapot.hiba = null;
      rajzol();
      try {
        await allapot.onTudomas(k.annId);
        allapot.kozlemenyek = allapot.kozlemenyek.filter((x) => x.annId !== k.annId);
        allapot.eredetiMutat = false;
      } catch (err) {
        log.error("[közlemény] a tudomásul vétel nem sikerült:", err);
        allapot.hiba = t(uiLang, "ann.ackFailed");
      } finally {
        allapot.foglalt = false;
        rajzol();
      }
    });
    gombok.appendChild(kesobb);
    gombok.appendChild(tudomas);
    kartya.appendChild(gombok);
  }

  function frissit(uj) {
    const eddigi = allapot.kozlemenyek[allapot.index];
    allapot = { ...allapot, ...uj, hiba: null };
    const cel = uj.kijelolt || (eddigi && eddigi.annId);
    const i = cel ? allapot.kozlemenyek.findIndex((x) => x.annId === cel) : -1;
    if (i >= 0) { if (!eddigi || eddigi.annId !== cel) allapot.eredetiMutat = false; allapot.index = i; }
    else allapot.index = Math.min(allapot.index, Math.max(allapot.kozlemenyek.length - 1, 0));
    rajzol();
  }

  function bezar() {
    document.removeEventListener("keydown", billentyu);
    gyoker.remove();
    if (olvaso && olvaso.gyoker === gyoker) olvaso = null;
  }
  function billentyu(e) { if (e.key === "Escape") bezar(); }
  document.addEventListener("keydown", billentyu);

  document.body.appendChild(gyoker);
  olvaso = { gyoker, bezar, frissit };
  frissit(p);
  return olvaso;
}
